/* eslint-disable react/no-unknown-property */
/* ============================================================
   WavingFlag – cloth simulation (vertex wave) + accurate canvas
   flag artwork. Hoist edge is pinned at x = 0, free edge waves.
   Flagpole – pole + finial + flag helper.
   ============================================================ */
import { useEffect, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { getFlagTexture } from "@/components/world/textures";

export function WavingFlag({
  kind = "greinch",
  width = 2.4,
  height = 1.6,
  phase = 0,
  position = [0, 0, 0],
  windSpeed = 1,
  segments = [22, 10],
}) {
  const geo = useMemo(() => {
    const g = new THREE.PlaneGeometry(width, height, segments[0], segments[1]);
    g.translate(width / 2, 0, 0); // hoist edge on the pole
    return g;
  }, [width, height, segments]);
  const base = useMemo(() => Float32Array.from(geo.attributes.position.array), [geo]);
  const tex = useMemo(() => getFlagTexture(kind), [kind]);

  useEffect(() => () => geo.dispose(), [geo]);

  useFrame((state) => {
    const t = state.clock.elapsedTime * 2.4 * windSpeed + phase;
    const pos = geo.attributes.position;
    const arr = pos.array;
    for (let i = 0; i < pos.count; i++) {
      const x = base[i * 3];
      const y = base[i * 3 + 1];
      const f = x / width; // 0 at the pole → 1 at the free edge
      const amp = f * f * 0.85 + f * 0.15;
      const w1 = Math.sin(f * 6.5 - t * 1.9 + y * 0.6) * 0.17;
      const w2 = Math.sin(f * 12.0 - t * 3.3 + y * 2.4) * 0.05;
      arr[i * 3 + 2] = (w1 + w2) * amp * width * 0.5;
      arr[i * 3 + 1] = y + Math.sin(f * 3.2 - t * 1.3) * 0.045 * amp * height;
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  });

  return (
    <mesh geometry={geo} position={position}>
      <meshStandardMaterial map={tex} side={THREE.DoubleSide} roughness={0.85} metalness={0} />
    </mesh>
  );
}

export function Flagpole({
  kind,
  height = 6,
  flagWidth = 2.4,
  flagHeight = 1.6,
  position = [0, 0, 0],
  phase = 0,
  rotation = 0,
}) {
  return (
    <group position={position} rotation={[0, rotation, 0]}>
      <mesh position={[0, height / 2, 0]}>
        <cylinderGeometry args={[0.05, 0.09, height, 10]} />
        <meshStandardMaterial color="#D9D6D0" metalness={0.9} roughness={0.25} />
      </mesh>
      {/* base collar */}
      <mesh position={[0, 0.12, 0]}>
        <cylinderGeometry args={[0.18, 0.24, 0.24, 12]} />
        <meshStandardMaterial color="#7A756E" roughness={0.8} />
      </mesh>
      {/* gold finial */}
      <mesh position={[0, height + 0.12, 0]}>
        <sphereGeometry args={[0.13, 12, 10]} />
        <meshStandardMaterial color="#E8C36A" metalness={1} roughness={0.2} />
      </mesh>
      <WavingFlag
        kind={kind}
        width={flagWidth}
        height={flagHeight}
        phase={phase}
        position={[0.06, height - flagHeight / 2 - 0.12, 0]}
      />
    </group>
  );
}
